import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { PageSEO, OrganizationSchema } from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Unique category images - each image used only once
const CATEGORY_IMAGES = {
  "Fitness & Active Spaces": "https://images.unsplash.com/photo-1761971974992-6df33df97c3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "Spa & Wellness": "https://images.unsplash.com/photo-1600334129128-685c5582fd35?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "Salons & Beauty": "https://images.pexels.com/photos/33412989/pexels-photo-33412989.png?auto=compress&cs=tinysrgb&w=800",
  "Hotel & Hospitality": "https://images.pexels.com/photos/29870245/pexels-photo-29870245.jpeg?auto=compress&cs=tinysrgb&w=800",
  "Restaurants & Fine Dining": "https://images.pexels.com/photos/33033776/pexels-photo-33033776.jpeg?auto=compress&cs=tinysrgb&w=800",
  "Corporate & Office": "https://images.pexels.com/photos/35058546/pexels-photo-35058546.jpeg?auto=compress&cs=tinysrgb&w=800",
  "Retail & Boutiques": "https://images.unsplash.com/photo-1769107805412-90d9191d53e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "Residential & Homes": "https://images.unsplash.com/photo-1758915753451-b478b4e6f920?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "Lounges & Member Clubs": "https://images.pexels.com/photos/19689234/pexels-photo-19689234.jpeg?auto=compress&cs=tinysrgb&w=800"
};

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [machines, setMachines] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchMachines();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/space-categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([
        "Fitness & Active Spaces",
        "Spa & Wellness",
        "Salons & Beauty",
        "Hotel & Hospitality",
        "Restaurants & Fine Dining",
        "Corporate & Office",
        "Retail & Boutiques",
        "Residential & Homes",
        "Lounges & Member Clubs"
      ]);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/diffuser-machines`);
      setMachines(response.data.machines.slice(0, 2));
    } catch (error) {
      console.error("Error fetching machines:", error);
    }
  };

  const getCategoryImage = (category) => {
    return CATEGORY_IMAGES[category] || null;
  };

  return (
    <div data-testid="home-page">
      <PageSEO page="home" />
      <OrganizationSchema />
      
      {/* Hero Section */}
      <section 
        className="hero-section relative"
        data-testid="hero-section"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1714636608872-048fc9231892?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        role="img"
        aria-label="Ikara Scents premium space fragrance oils for hotels spas and commercial spaces India"
      >
        <div className="hero-overlay" />
        <div className="hero-content container-luxury text-center">
          <p className="text-overline mb-6 animate-fade-in">Artisanal Space Scenting</p>
          <h1 className="text-display mb-8 animate-fade-in animation-delay-100">
            Scent is the first thing<br />
            your guests notice.<br />
            Make it intentional.
          </h1>
          <p className="text-subtitle max-w-2xl mx-auto mb-12 animate-fade-in animation-delay-200">
            Signature fragrance oils and diffusion systems for India's finest commercial spaces.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animation-delay-300">
            <Link 
              to="/products" 
              className="btn-luxury inline-flex items-center justify-center gap-2"
              data-testid="hero-explore-btn"
            >
              Explore Fragrances
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/contact" 
              className="btn-luxury-outline inline-flex items-center justify-center"
              data-testid="hero-enquire-btn"
            >
              Enquire Now
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="section-luxury" data-testid="brand-story-section">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-overline mb-6">Our Philosophy</p>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl mb-8">
              The Art of Scenting Spaces
            </h2>
            <div className="divider-luxury mx-auto" />
            <p className="text-subtitle leading-relaxed">
              Every space tells a story. At Ikara Scents, we believe that fragrance 
              is the invisible thread that weaves memories, emotions, and experiences 
              into the fabric of your environment. Our artisanal approach combines 
              traditional perfumery with modern diffusion technology, creating 
              signature scents that become an integral part of your brand identity.
            </p>
          </div>
        </div>
      </section>

      {/* Space Categories Section */}
      <section 
        className="section-luxury" 
        style={{ backgroundColor: 'var(--surface)' }}
        data-testid="categories-section"
      >
        <div className="container-luxury">
          <div className="text-center mb-16">
            <p className="text-overline mb-6">Spaces We Scent</p>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl">
              Nine Distinct Environments
            </h2>
          </div>

          <div className="grid-categories">
            {categories.map((category, index) => (
              <Link
                key={category}
                to={`/products?category=${encodeURIComponent(category)}`}
                className="category-card aspect-category group"
                data-testid={`category-${index}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <img
                  src={getCategoryImage(category)}
                  alt={`Ikara Scents fragrance oils for ${category} - premium commercial space scenting`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="category-overlay">
                  <h3 className="font-heading text-xl md:text-2xl text-white font-light">
                    {category}
                  </h3>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/products" 
              className="btn-luxury-outline inline-flex items-center gap-2"
              data-testid="view-all-scents-btn"
            >
              View All Scents
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Machines Section */}
      <section className="section-luxury" data-testid="machines-section">
        <div className="container-luxury">
          <div className="text-center mb-16">
            <p className="text-overline mb-6">Diffuser Technology</p>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl">
              Precision Engineering
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16">
            {machines.map((machine, index) => (
              <div 
                key={machine.name}
                className="product-item border-0 py-12"
                data-testid={`machine-${index}`}
              >
                <h3 className="font-heading text-3xl lg:text-4xl font-light mb-4">
                  {machine.name}
                </h3>
                <p className="text-overline mb-4">{machine.coverage}</p>
                <p className="text-subtitle mb-8">{machine.description}</p>
                <Link 
                  to="/contact" 
                  className="btn-luxury-outline inline-flex items-center gap-2"
                  data-testid={`machine-enquire-${index}`}
                >
                  Enquire Now
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/products" 
              className="btn-luxury inline-flex items-center gap-2"
              data-testid="view-all-machines-btn"
            >
              View All Machines
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section-luxury text-center"
        style={{ backgroundColor: 'var(--primary-text)' }}
        data-testid="cta-section"
      >
        <div className="container-luxury">
          <p 
            className="text-overline mb-6"
            style={{ color: 'var(--accent)' }}
          >
            Begin Your Journey
          </p>
          <h2 
            className="text-display text-3xl sm:text-4xl lg:text-5xl mb-8"
            style={{ color: 'var(--background)' }}
          >
            Create Your Signature Scent
          </h2>
          <p 
            className="text-subtitle max-w-2xl mx-auto mb-12"
            style={{ color: 'var(--accent)' }}
          >
            Let us craft a bespoke olfactory identity for your space. 
            Our experts will guide you through our collection to find 
            the perfect fragrance that embodies your brand.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-sm tracking-widest uppercase font-medium transition-all hover:opacity-90"
            style={{ 
              backgroundColor: 'var(--background)', 
              color: 'var(--primary-text)',
              letterSpacing: '0.15em'
            }}
            data-testid="cta-contact-btn"
          >
            Enquire Now
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
